import React, { useState } from 'react';

import { BookingData } from '@types';
import { ProgressIndicator } from '_atoms';
import { ProgressStep } from '_atoms/ProgressIndicator';
import { useAppSelector } from '_hooks';

import {
  BookingSubmissionStatus,
  Payment,
  SelectSessionDateTime,
  SkatersInfo,
  TicketCheckoutEntry,
  TicketConfirmation,
  WaiverNew,
} from '_molecules';
import TicketService from '_services/TicketService';
import HelperMethods from '_utils/HelperMethods';

export type BookingStep =
  | 'selectSessionDateTime'
  | 'skatersInfo'
  | 'waiver'
  | 'processingBooking'
  | 'payment'
  | 'confirmation';

const STEPS: ProgressStep[] = [
  {
    id: 'selectSessionDateTime',
    label: 'Select Session',
    description: 'Choose your preferred date and time',
  },
  {
    id: 'skatersInfo',
    label: 'Skaters Info',
    description: 'Enter skater information',
  },
  {
    id: 'waiver',
    label: 'Waiver',
    description: 'Sign the waiver agreement',
  },
  {
    id: 'payment',
    label: 'Payment',
    description: 'Complete your payment',
  },
];

const TicketBookingFlow: React.FC = () => {
  const isAuthenticated = useAppSelector(state => state.auth.isAuthenticated);
  const [continueAsGuest, setContinueAsGuest] = useState(false);
  const [currentStep, setCurrentStep] = useState<BookingStep>(
    'selectSessionDateTime',
  );
  const [bookingData, setBookingData] = useState<BookingData>(() => {
    const ticketBarcode = HelperMethods.generateRandom6DigitNumber();
    return {
      barcodeId: `tb${ticketBarcode.toString()}`,
    };
  });

  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [capacityError, setCapacityError] = useState('');

  const showCheckoutEntry = !isAuthenticated && !continueAsGuest;

  const handleNext = (stepData?: Partial<BookingData>) => {
    // Validate capacity when moving from skatersInfo step
    if (currentStep === 'skatersInfo' && stepData?.skatersInfo) {
      const selectedTimeSlot = bookingData.selectedTimeSlot;
      const numSelectedSkaters = stepData.skatersInfo.length;

      if (
        selectedTimeSlot &&
        numSelectedSkaters > selectedTimeSlot.maxCapacity
      ) {
        setCapacityError(
          `You have selected ${numSelectedSkaters} ticket${numSelectedSkaters > 1 ? 's' : ''}, but only ${selectedTimeSlot.maxCapacity} ${selectedTimeSlot.maxCapacity === 1 ? 'is' : 'are'} currently available for this session. Please adjust the quantity to ${selectedTimeSlot.maxCapacity} or fewer.`,
        );
        return; // Prevent moving to next step
      } else {
        setCapacityError(''); // Clear error if validation passes
      }
    }

    if (stepData) {
      setBookingData(prev => ({ ...prev, ...stepData }));
    }

    const currentIndex = STEPS.findIndex(step => step.id === currentStep);

    if (currentIndex < STEPS.length - 1) {
      setCurrentStep(STEPS[currentIndex + 1].id as BookingStep);
      // Scroll to top for mobile users
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevious = () => {
    setCapacityError(''); // Clear capacity error when going back
    const currentIndex = STEPS.findIndex(step => step.id === currentStep);

    if (currentIndex > 0) {
      setCurrentStep(STEPS[currentIndex - 1].id as BookingStep);
      // Scroll to top for mobile users
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleStepComplete = (stepData: Partial<BookingData>) => {
    setBookingData(prev => ({ ...prev, ...stepData }));
  };

  const processPayment = (stepData: Partial<BookingData>) => {
    const updatedData = { ...bookingData, ...stepData };
    setBookingData(updatedData);

    console.log('Updated booking data with payment info:', updatedData);

    setCurrentStep('confirmation');
    // Scroll to top for mobile users
    window.scrollTo({ top: 0, behavior: 'smooth' });

    submitTicketBookingDataWithData(updatedData);
  };

  const submitTicketBookingDataWithData = async (data: BookingData) => {
    if (
      (!data.isSkatingWaiverAttached || !data.attachedWaivers) &&
      (!data.skaterWaivers || data.skaterWaivers.length === 0)
    ) {
      setSubmitError('No waiver data to submit');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Create FormData to send to the API
      const formData = new FormData();

      // Option 1: Send as JSON string (current approach)
      formData.append(
        'bookingData',
        JSON.stringify({
          barcodeId: data.barcodeId,
          selectedTimeSlot: data.selectedTimeSlot,
          skatersInfo: data.skatersInfo,
          waiverAccepted: data.waiverAccepted,
          skaterWaivers: data.skaterWaivers,
          isSkatingWaiverAttached: data.isSkatingWaiverAttached,
          skaterWaiverAttached: data.skaterWaiverAttached,
          attachedWaivers: data.attachedWaivers,
          paymentInfo: data.paymentInfo,
          couponAttached: data.couponAttached,
          selectedSkatingPasses: data.selectedSkatingPasses,
          submittedAt: new Date().toISOString(),
        }),
      );

      // APPROACH 1: File Objects (Recommended for most cases)
      // Pros:
      // - Smaller payload size (files are compressed)
      // - Better for large files
      // - Standard way to handle file uploads
      // - Browser handles file encoding automatically
      // Cons:
      // - Requires multipart/form-data
      // - Server needs to handle file uploads
      for (const waiver of data.skaterWaivers || []) {
        if (waiver.signedPDF) {
          formData.append(
            'signedPDFs',
            waiver.signedPDF,
            `${waiver.skaterName}_waiver.pdf`,
          );
        }
      }

      // Submit to API using TicketService
      const response = await TicketService.createTicketBooking(formData);

      console.log('Ticket booking submitted successfully:', response);

      // Set submitted state and navigate to success step
      setIsSubmitted(true);
    } catch (error: any) {
      console.error('Error submitting waiver:', error);
      setSubmitError(
        error?.detail ||
          error?.message ||
          error?.result?.message ||
          'An error occurred. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'selectSessionDateTime':
        return (
          <SelectSessionDateTime
            selectedTimeSlot={bookingData.selectedTimeSlot}
            onNext={stepData => {
              setCapacityError(''); // Clear capacity error when selecting a new session
              handleNext(stepData);
            }}
            onStepComplete={handleStepComplete}
          />
        );
      case 'skatersInfo':
        return (
          <>
            {capacityError && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start">
                  <svg
                    className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-red-800 mb-1">
                      Capacity Exceeded
                    </h3>
                    <p className="text-sm text-red-700">{capacityError}</p>
                  </div>
                </div>
              </div>
            )}
            <SkatersInfo
              skatersInfo={bookingData.skatersInfo}
              selectedTimeSlot={bookingData.selectedTimeSlot}
              onNext={handleNext}
              onPrevious={handlePrevious}
              onStepComplete={handleStepComplete}
            />
          </>
        );
      case 'waiver':
        return (
          <WaiverNew
            skatersInfo={bookingData.skatersInfo}
            onPrevious={handlePrevious}
            isInFlow={true}
            onContinue={waiverData => {
              // Check if ONLY existing waivers were attached (no new form filled)
              if (waiverData.isExistingWaiver && !waiverData.hasMixedWaivers) {
                // Use the attached waivers directly
                handleNext({
                  waiverAccepted: true,
                  isSkatingWaiverAttached: true,
                  attachedWaivers: waiverData.attachedWaivers,
                });
                return;
              }

              // Handle mixed scenario or new waiver only
              const skaterWaivers = [];

              // Add new waiver data if it exists (from filled form)
              if (waiverData.adult && waiverData.signedPDF) {
                // Add adult waiver
                const adultWaiver = {
                  skaterId: '1',
                  skaterName: `${waiverData.adult.firstName} ${waiverData.adult.lastName}`,
                  dateOfBirth: waiverData.adult.dateOfBirth,
                  isAdult: true,
                  hasReadWaiver: waiverData.adult.hasReadWaiver,
                  accepted: waiverData.adult.accepted,
                  signature: waiverData.adult.signature,
                  signedBy: waiverData.adult.signedBy,
                  relationship: 'Self',
                  email: waiverData.adult.email,
                  phoneNumber: waiverData.adult.phoneNumber,
                  emergencyContactPhone: waiverData.adult.emergencyContactPhone,
                  signedPDF: waiverData.signedPDF,
                  barcodeId: waiverData.barcodeId,
                  minors: waiverData.minors.map(
                    (_m: any, idx: number) => `minor-${idx + 2}`,
                  ),
                  waiverId: waiverData.waiverId,
                };
                skaterWaivers.push(adultWaiver);

                // Add minor waivers (they reference the adult's PDF)
                waiverData.minors.forEach((minor: any, idx: number) => {
                  const minorWaiver = {
                    skaterId: `minor-${idx + 2}`,
                    skaterName: `${minor.firstName} ${minor.lastName}`,
                    dateOfBirth: minor.dateOfBirth,
                    isAdult: false,
                    hasReadWaiver: waiverData.adult.hasReadWaiver,
                    accepted: waiverData.adult.accepted,
                    signature: waiverData.adult.signature,
                    signedBy: waiverData.adult.signedBy,
                    relationship: 'Guardian',
                    email: waiverData.adult.email,
                    phoneNumber: waiverData.adult.phoneNumber,
                    emergencyContactPhone:
                      waiverData.adult.emergencyContactPhone,
                    signedPDF: waiverData.signedPDF,
                    barcodeId: waiverData.barcodeId,
                    minors: [],
                    waiverId: waiverData.waiverId,
                  };
                  skaterWaivers.push(minorWaiver);
                });
              }

              handleNext({
                waiverAccepted: true,
                ...(skaterWaivers.length > 0 && { skaterWaivers }),
                isSkatingWaiverAttached:
                  waiverData.attachedWaivers !== null &&
                  waiverData.attachedWaivers !== undefined,
                attachedWaivers: waiverData.attachedWaivers,
              });
            }}
          />
        );
      case 'payment':
        return (
          <Payment
            paymentType="skating"
            bookingData={bookingData}
            onPrevious={handlePrevious}
            onStepComplete={processPayment}
          />
        );
      case 'confirmation':
        return (
          <BookingSubmissionStatus
            isSubmitting={isSubmitting}
            isSubmitted={isSubmitted}
            submitError={submitError}
            onTryAgain={() => setCurrentStep('payment')}
            onStartOver={() => setCurrentStep('selectSessionDateTime')}
          >
            <TicketConfirmation
              bookingData={bookingData}
              ticketNumber={bookingData.barcodeId}
            />
          </BookingSubmissionStatus>
        );
      default:
        return null;
    }
  };

  if (showCheckoutEntry) {
    return (
      <div className="max-w-4xl mx-auto p-0 md:p-6">
        <TicketCheckoutEntry
          onContinueAsGuest={() => setContinueAsGuest(true)}
        />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-0 md:p-6">
      <p className="text-center text-gray-600 mb-6">
        Select your preferred time and complete your booking
      </p>
      {/* Progress Indicator */}
      <ProgressIndicator steps={STEPS} currentStep={currentStep} />
      {/* Current Step Content */}
      <div className="bg-white rounded-lg shadow-lg p-2 md:p-6">
        {renderCurrentStep()}
      </div>
      {/* Debug: Show waiver data */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mt-4 p-4 bg-gray-100 rounded">
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            Debug Data:
          </h3>
          <pre className="text-xs text-gray-600 overflow-auto">
            {JSON.stringify(bookingData, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export default TicketBookingFlow;
